import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { BookMarked, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSeriesProgress } from '@/hooks/useSeriesProgress';

interface SeriesContextBannerProps {
  bookId: string;
  bookTitle?: string;
  seriesName?: string;
  seriesPosition?: number;
  seriesTotal?: number;
  authors?: string[];
}

export function SeriesContextBanner({
  bookId,
  bookTitle,
  seriesName,
  seriesPosition,
  seriesTotal,
  authors,
}: SeriesContextBannerProps) {
  const { allSeries } = useSeriesProgress();

  if (!seriesName || !seriesPosition || !authors?.length) {
    return null;
  }

  // Don't show banner if series name is the same as the book title (data quality issue)
  const normalizedTitle = bookTitle?.toLowerCase().trim();
  const normalizedSeriesName = seriesName.toLowerCase().trim();
  if (normalizedTitle && normalizedSeriesName === normalizedTitle) {
    return null;
  }

  const primaryAuthor = authors[0];

  // Find if user has this series in their library
  const trackedSeries = allSeries.find(
    (s) =>
      s.seriesName === normalizedSeriesName &&
      s.authorName.toLowerCase() === primaryAuthor.toLowerCase() &&
      !s.isDismissed
  );

  if (!trackedSeries) {
    return null;
  }

  const progressPercent = trackedSeries.totalKnown
    ? Math.round((trackedSeries.positionsRead.length / trackedSeries.totalKnown) * 100)
    : 0;

  const isCurrentBookRead = trackedSeries.positionsRead.includes(seriesPosition);
  const booksRemaining = trackedSeries.totalKnown
    ? trackedSeries.totalKnown - trackedSeries.positionsRead.length
    : null;

  return (
    <Card className="card-elevated border-primary/20 bg-primary/5">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-primary/10 shrink-0">
            <BookMarked className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <div>
              <p className="text-sm text-muted-foreground">
                Part of your series
              </p>
              <p className="font-semibold line-clamp-1">
                {seriesName} (Book {seriesPosition}
                {seriesTotal ? ` of ${seriesTotal}` : ''})
              </p>
              <p className="text-sm text-muted-foreground">
                by {primaryAuthor}
              </p>
            </div>

            {trackedSeries.totalKnown && (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Your Progress</span>
                  <span className="font-medium">
                    {trackedSeries.positionsRead.length} of {trackedSeries.totalKnown} books
                  </span>
                </div>
                <Progress value={progressPercent} className="h-2" />
                {booksRemaining !== null && booksRemaining > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {booksRemaining} {booksRemaining === 1 ? 'book' : 'books'} remaining
                  </p>
                )}
              </div>
            )}

            {isCurrentBookRead && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                ✓ You've already read this book
              </p>
            )}
          </div>

          <Button variant="outline" size="sm" asChild className="shrink-0">
            <Link to="/series-tracker" className="flex items-center gap-1">
              View Series
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
